import styled from "@emotion/styled";

import { theme } from "../../../Internals/ThemeProvider/CandyTheme/ThemeShorthand";
import { detectIE } from "../../../../Engine/utils";
import { ScrollableContainer2 } from "../../../Internals/ScrollableContainer2/ScrollableContainer2";
import { ThrowClassNameToChildren } from "../../HiddenPanel/ThrowClassNameToChild";

interface UnitsLayoutItemProps {
    minHeight?: string | number;
    maxWidth?: string | number;
    marginTop?: string | number;
    layoutKind: "simple" | "wide" | "scrollable" | "formInForm";
    showBorder: boolean;
    drawAsPageStack?: boolean;
}

export const DEFAULT_CONTENT_WIDTH_IN_PX = 1000;
export const UNIT_DEFAULT_PADDING = "60px 40px 80px";
export const DEFAULT_CONTENT_LAYOUT = "scrollable";

export const UnitsLayoutItem = styled.section<UnitsLayoutItemProps>(
    props => ({
        marginTop: props.marginTop ?? 40,
        ":first-of-type": { marginTop: props.marginTop ? `calc(${props.marginTop}px - 40px)` : 0 },
        marginLeft: "auto",
        marginRight: "auto",
        marginBottom: 0,

        position: "relative",
        minHeight: props.minHeight,
        background: theme(props).keyPalette.primaryBackgroundColor,
        boxShadow: `0 5px 8px 2px ${theme(props).legacy.shadowColor}`,

        [`${UnitContentInner}`]: {
            padding: UNIT_DEFAULT_PADDING,
            position: "relative",
        },
        [`${UnitContentInnerScrollable}`]: {
            padding: UNIT_DEFAULT_PADDING,
            position: "relative",
        },
    }),
    props =>
        props.drawAsPageStack && {
            boxShadow: [
                `0 5px 8px 2px ${theme(props).legacy.shadowColor}`,
                `10px -10px 0px 0px ${theme(props).keyPalette.primaryBackgroundColor}`,
                `10px -5px 8px 2px ${theme(props).legacy.shadowColor}`,
                `20px -20px 0px 0px ${theme(props).keyPalette.primaryBackgroundColor}`,
                `20px -15px 8px 2px ${theme(props).legacy.shadowColor}`,
            ].join(", "),
        },
    props =>
        props.layoutKind === "simple" && {
            maxWidth: props.maxWidth ?? DEFAULT_CONTENT_WIDTH_IN_PX,
            overflowX: "auto",

            [`${UnitContentInner}`]: {
                overflow: "hidden",
            },
            [`${UnitContentInnerScrollable}`]: {
                overflow: "hidden",
            },
        },
    props =>
        props.layoutKind === "scrollable" && {
            maxWidth: props.maxWidth ?? DEFAULT_CONTENT_WIDTH_IN_PX,
        },
    props =>
        props.layoutKind === "wide" && {
            maxWidth: props.maxWidth && wrapMaxWidthWithDefaultValue(props.maxWidth),
            [`${UnitContentInnerScrollable}`]: {
                overflow: "hidden",
            },
        },

    props =>
        props.layoutKind === "formInForm" && {
            maxWidth: props.maxWidth ?? "max-content",

            [`${UnitContentInner}:not(${UnitContentInner} ${UnitContentInner})`]: {
                padding: "0",
                overflow: "hidden",
            },
        },

    props =>
        !props.showBorder && {
            boxShadow: "none",

            [`${UnitContentInner}`]: {
                padding: "0 0 20px",
            },
            [`${UnitContentInnerScrollable}`]: {
                padding: "0 0 20px",
            },
        }
);

const mainTag = detectIE() ? "div" : "main";

export const UnitsLayout = styled(mainTag)({
    paddingTop: 40,
    paddingBottom: 40,
});

export const UnitContentInner = styled.div();

export const TopUnitsNavigatorContainer = styled.div(props => ({
    marginTop: theme(props).size(4),
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
}));

export const BottomUnitsNavigatorContainer = styled(ThrowClassNameToChildren)(props => ({
    marginBottom: theme(props).size(4),
    marginTop: theme(props).size(2),
    marginLeft: "auto",
    marginRight: "auto",
    textAlign: "center",
}));

export const UnitContentInnerScrollable = UnitContentInner.withComponent(ScrollableContainer2);

export const PageActionsContainer = styled.div({
    position: "absolute",
    right: 25,
    top: 25,
    zIndex: 999,
});

export const UnitAdderContainer = styled.div(() => ({
    marginTop: 40,
    marginBottom: 0,
    marginLeft: "auto",
    marginRight: "auto",
}));

function wrapMaxWidthWithDefaultValue(maxWidth: string | number | undefined) {
    const castedMaxWidth = Number(maxWidth);
    return castedMaxWidth > DEFAULT_CONTENT_WIDTH_IN_PX ? castedMaxWidth : DEFAULT_CONTENT_WIDTH_IN_PX;
}
